import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const MARGIN = 8; // mm
const GAP = 4; // mm between blocks

const canvasOf = (el) =>
  html2canvas(el, {
    scale: 2,
    useCORS: true,
    backgroundColor: "#ffffff",
    ignoreElements: (node) => node.classList && node.classList.contains("pdf-hide"),
  });

// Renders an element to a multi-page A4 PDF, paginating block-by-block so that
// individual cards/sections are never cut across two pages. A block taller than
// one page is scaled down to fit a full page (content stays complete).
export async function exportElementToPdf(element, filename = "export") {
  if (!element) return;
  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const contentW = pageW - MARGIN * 2;
  const usableH = pageH - MARGIN * 2;

  let cursorY = MARGIN;
  let placedAny = false;

  const place = (canvas) => {
    if (!canvas.width || !canvas.height) return;
    let drawW = contentW;
    let drawH = (canvas.height * drawW) / canvas.width;
    if (drawH > usableH) {
      drawH = usableH;
      drawW = (canvas.width * drawH) / canvas.height;
    }
    if (placedAny && cursorY + drawH > pageH - MARGIN) {
      pdf.addPage();
      cursorY = MARGIN;
    }
    const x = MARGIN + (contentW - drawW) / 2;
    pdf.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", x, cursorY, drawW, drawH);
    cursorY += drawH + GAP;
    placedAny = true;
  };

  // predicted rendered height in mm at the target content width
  const predictH = (el) => {
    const w = el.clientWidth || el.offsetWidth;
    if (!w) return 0;
    return el.offsetHeight * (contentW / w);
  };

  const renderableChildren = (el) =>
    Array.from(el.children).filter(
      (c) => c.offsetHeight > 0 && !(c.classList && c.classList.contains("pdf-hide"))
    );

  const addBlock = async (el, depth) => {
    if (predictH(el) <= usableH) {
      place(await canvasOf(el));
      return;
    }
    const children = renderableChildren(el);
    // Unwrap single-child containers so we reach the real content blocks
    // instead of capturing an over-tall wrapper as one (blank) canvas.
    if (children.length === 1) {
      await addBlock(children[0], depth);
      return;
    }
    if (children.length === 0 || depth >= 6) {
      place(await canvasOf(el));
      return;
    }
    for (const child of children) await addBlock(child, depth + 1);
  };

  const roots = renderableChildren(element);
  if (roots.length === 0) {
    place(await canvasOf(element));
  } else {
    for (const r of roots) await addBlock(r, 1);
  }

  pdf.save(`${filename}.pdf`);
}
